import { BaseAPI } from 'API/BaseApi';
import { eAPITags } from 'API/eAPITags';
import { IFpeStatusData, IFpeStatusesData } from 'types/order/fpeStatuses/data/IFpeStatusesData';
import {
  IBalancingClosingUpdateRequest,
  IDisbursementUpdateRequest,
  IFundingAuthorizationUpdateRequest,
  IPreliminaryFeesSentUpdateRequest,
} from 'types/order/fpeStatuses/request/IFpeStatusesRequest';
import { IFpeStatusesResponse } from 'types/order/fpeStatuses/response/IFpeStatusesResponse';

const BASE_URL = '/timber/api/internal/v1/fpe-status';

export const FpeStatusesAPI = BaseAPI.injectEndpoints({
  endpoints: (build) => {
    return {
      getFpeStatusesData: build.query<IFpeStatusesData, string>({
        query: (orderId) => `${BASE_URL}/${orderId}`,
        transformResponse: (baseReturnValue: IFpeStatusesResponse[], _, orderId) =>
          convertIFpeStatusesResponseToIFpeStatusesData(baseReturnValue, orderId),
        providesTags: (result, error, orderId) => [{ type: eAPITags.FpeStatuses, id: orderId }],
      }),
      updatePreliminaryFees: build.mutation<IFpeStatusesData, IPreliminaryFeesSentUpdateRequest>({
        query: (request) => ({
          url: `${BASE_URL}/preliminary-fees-sent/${request.orderId}`,
          method: 'PUT',
          body: {
            ...request,
            statusValue: mapStatusValue(request.statusValue),
          },
        }),
        invalidatesTags: (result, error, { orderId }) => [{ type: eAPITags.FpeStatuses, id: orderId }],
      }),
      updateBalancingClosing: build.mutation<IFpeStatusesData, IBalancingClosingUpdateRequest>({
        query: (request) => ({
          url: `${BASE_URL}/balancing-closing/${request.orderId}`,
          method: 'PUT',
          body: {
            ...request,
            statusValue: mapStatusValue(request.statusValue),
          },
        }),
        invalidatesTags: (result, error, { orderId }) => [{ type: eAPITags.FpeStatuses, id: orderId }],
      }),
      updateFundingAuthorization: build.mutation<IFpeStatusesData, IFundingAuthorizationUpdateRequest>({
        query: (request) => ({
          url: `${BASE_URL}/funding-authorization/${request.orderId}`,
          method: 'PUT',
          body: {
            ...request,
            statusValue: mapStatusValue(request.statusValue),
          },
        }),
        invalidatesTags: (result, error, { orderId }) => [{ type: eAPITags.FpeStatuses, id: orderId }],
      }),
      updateDisbursement: build.mutation<IFpeStatusesData, IDisbursementUpdateRequest>({
        query: (request) => ({
          url: `${BASE_URL}/disbursement/${request.orderId}`,
          method: 'PUT',
          body: {
            ...request,
            statusValue: mapStatusValue(request.statusValue),
          },
        }),
        invalidatesTags: (result, error, { orderId }) => [{ type: eAPITags.FpeStatuses, id: orderId }],
      }),
    };
  },
});
export const {
  useGetFpeStatusesDataQuery,
  useUpdatePreliminaryFeesMutation,
  useUpdateBalancingClosingMutation,
  useUpdateFundingAuthorizationMutation,
  useUpdateDisbursementMutation,
} = FpeStatusesAPI;

const convertResponseToData = (response?: IFpeStatusesResponse): IFpeStatusData => {
  if (!response) {
    return { value: '', createdAt: '', createdBy: '', updatedAt: '', updatedBy: '' };
  }
  const { statusValue: value, createdAt, createdBy, updatedAt, updatedBy } = response;
  return { value, createdAt, createdBy, updatedAt, updatedBy };
};

const convertIFpeStatusesResponseToIFpeStatusesData = (
  response: IFpeStatusesResponse[],
  orderId: string
): IFpeStatusesData => {
  const preliminaryFeesSent = response.find((entry) => entry.statusType === 'preliminaryFeesSent');
  const balancingClosing = response.find((entry) => entry.statusType === 'balancingClosing');
  const fundingAuthorization = response.find((entry) => entry.statusType === 'fundingAuthorization');
  const disbursement = response.find((entry) => entry.statusType === 'disbursement');
  const fundingComplete = response.find((t) => t.statusType === 'fundingComplete');
  return {
    orderId,
    preliminaryFeesSent: convertResponseToData(preliminaryFeesSent),
    balancingClosing: convertResponseToData(balancingClosing),
    fundingAuthorization: convertResponseToData(fundingAuthorization),
    disbursement: convertResponseToData(disbursement),
    fundingComplete: convertResponseToData(fundingComplete),
  };
};

const mapStatusValue = (statusValue: string) => statusValue.toLocaleUpperCase().replaceAll(' ', '_');
